import React, { useState, useContext, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { lightTheme, darkTheme } from "../../assets/styles/Theme.tsx";
import { ThemeContext } from "../../utils/common/ThemeContext.tsx";
import Dashboard from "../../features/dashboard/Dashboard.tsx";
import Sidebar from "./Sidebar.tsx";
import Header from "./Header.tsx";
import { CssBaseline, Box, ThemeProvider, useMediaQuery, Theme } from "@mui/material";
import Login from "../../features/dashboard/Login.tsx";
import HideSidebar from "../../utils/common/HideSidebar.tsx";
import { useLocation } from "react-router-dom";
import { PageDataProvider } from "../../utils/common/PageDataContext.tsx";
import CreateBrands from "../../features/settings/CreateBrands.tsx";
import CreateCategory from "../../features/settings/CreateCategory.tsx";
import CreateSubCategory from "../../features/settings/CreateSubCategory.tsx";
import CreateIndustry from "../../features/settings/CreateIndustry.tsx";
import CreateMeasuringUnits from "../../features/settings/CreateMeasuringUnits.tsx";
import CreatePackages from "../../features/settings/CreatePackages.tsx";
import CreateUom from "../../features/settings/CreateUom.tsx";
import CreateProducts from "../../features/settings/CreateProducts.tsx";
import CreateDocumentTypes from "../../features/settings/CreateDocumentTypes.tsx";
import ViewCreateUom from "../../features/settings/ViewCreateUom.tsx";
import ViewBrands from "../../features/settings/ViewBrands.tsx";
import ViewCategory from "../../features/settings/ViewCategory.tsx";
import ViewDocumentType from "../../features/settings/ViewDocumentType.tsx";
import ViewMeasuringunit from "../../features/settings/ViewMeasuringunit.tsx";
import ViewPackage from "../../features/settings/ViewPackage.tsx";
import ViewProduct from "../../features/settings/ViewProduct.tsx";
import ViewIndustry from "../../features/settings/ViewIndustry.tsx";
import ViewSubcatergory from "../../features/settings/ViewSubcatergory.tsx";
import ProductDoc from "../products/ProductDoc";
import CreateReason from "../../features/settings/CreateReason.tsx";
import ViewReason from "../../features/settings/ViewReason.tsx";
import ViewMainCategory from "../../features/settings/ViewMainCategory.tsx";
import CreateMainCategory from "../../features/settings/CreateMainCategory.tsx";
import CreateGenaricName from "../../features/settings/CreateGenaricName.tsx";
import ViewGenaricName from "../../features/settings/ViewGenaricName.tsx";
import CreateItem from "../../features/items/CreateItem.tsx";
import ViewCreateItem from "../../features/items/ViewCreateItem.tsx";
import ItemListView from "../../features/items/ItemListView.tsx";
import ViewItemData from "../../features/items/ViewItemData.tsx";
import DataMigration from "../../features/items/DataMigration.tsx";
import UnverifiedItems from "../../features/items/UnverifiedItems.tsx";
import CreateVariant from "../../features/items/CreateVariant.tsx";
import AddVariant from "../../features/items/AddVarient.tsx";

// Define types for ThemeContext
interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

// Define props for Sidebar (adjust these based on your actual Sidebar props)
interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
  mobileOpen?: boolean;
  handleDrawerToggle?: () => void;
  desktopOpen?: boolean;
  setDesktopOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  toggleTheme?: () => void;
  [key: string]: any; // Allow additional props
}

const drawerWidth: number = 240;

const PageRouting: React.FC = () => {
  const { mode, toggleTheme } = useContext(ThemeContext) as ThemeContextType;
  const theme: Theme = mode === "light" ? lightTheme : darkTheme;

  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [desktopOpen, setDesktopOpen] = useState<boolean>(true);
  const hideSidebar: boolean = HideSidebar();
  const location = useLocation();
  const isLoginPage: boolean = location.pathname === "/";
  const [pageTitle, setPageTitle] = useState<string>("");

  // Detect if screen is tab size (between sm and md)
  const isTabView: boolean = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Auto collapse sidebar on tab view
  useEffect(() => {
    if (isTabView) {
      setDesktopOpen(false);
    } else {
      setDesktopOpen(true);
    }
  }, [isTabView]);

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = (): void => {
    setDesktopOpen(!desktopOpen);
  };

  // Prepare sidebar props based on common patterns
  const sidebarProps = {
    // Common pattern 1: MUI Drawer props
    open: mobileOpen,
    onClose: handleDrawerToggle,
    
    // Common pattern 2: Custom props
    mobileOpen: mobileOpen,
    handleDrawerToggle: handleDrawerToggle,
    desktopOpen: desktopOpen,
    setDesktopOpen: setDesktopOpen,
    toggleTheme: toggleTheme,
  };

  return (
    <ThemeProvider theme={theme}>
      <PageDataProvider>
        <>
          <CssBaseline />

          {isLoginPage ? (
            // === LOGIN PAGE LAYOUT ===
            <Box
              sx={{
                height: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#fff",
              }}
            >
              <Routes>
                <Route path="/" element={<Login />} />
              </Routes>
            </Box>
          ) : (
            <Box
              sx={{
                display: { xs: "block", sm: "flex", md: "flex" },
                height: "100vh",
              }}
            >
              {!hideSidebar && (
                <Header
                  handleDrawerToggle={handleDrawerToggle}
                  handleDesktopToggle={handleDesktopToggle}
                  desktopOpen={desktopOpen}
                  pageTitle={pageTitle}
                />
              )}
              {!hideSidebar && (
                <Box
                  component="nav"
                  sx={{
                    width: { sm: desktopOpen ? drawerWidth : 0 },
                    backgroundColor: theme.palette.background.paper,
                    flexShrink: { sm: 0 },
                    borderRight: "none",
                  }}
                  aria-label="mailbox folders"
                >
                  {/* Type assertion to handle any prop mismatches */}
                  <Sidebar {...sidebarProps as any} />
                </Box>
              )}
              <Box
                component="main"
                sx={{
                  p: { xs: 0, sm: 0, md: 2 },
                  px: { xs: 2, sm: 2, md: 2 },
                  width: {
                    sm: `calc(100% - ${desktopOpen ? drawerWidth : 0}px)`,
                  },
                  transition: "width 0.3s",
                  mt: { xs: 8, sm: 10, md: 8, lg: 8, xl: 8 },
                  pb: { xs: 2, sm: 16, md: 0 },
                }}
              >
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/Login" element={<Login />} />
                  <Route path="/dashboard" element={<Dashboard />} />

                  {/* Settings Routes */}
                  <Route path="/vMainCategory" element={<ViewMainCategory />} />
                  <Route path="/CreateMainCategory" element={<CreateMainCategory />} />

                  <Route path="/vCreateCategory" element={<ViewCategory />} />
                  <Route path="/CreateCategory" element={<CreateCategory />} />
                  
                  <Route path="/CreateSubCategory" element={<CreateSubCategory />} />
                  <Route path="/vsubcategory" element={<ViewSubcatergory />} />

                  <Route path="/CreateIndustry" element={<CreateIndustry />} />
                  <Route path="/vCreateIndustry" element={<ViewIndustry />} />

                  <Route path="/vCreateMeasuringUnits" element={<ViewMeasuringunit />} />
                  <Route path="/CreateMeasuringUnits" element={<CreateMeasuringUnits />} />

                  <Route path="/uom" element={<ViewCreateUom />} />
                  <Route path="/CreateUom" element={<CreateUom />} />

                  <Route path="/vgenericnames" element={<ViewGenaricName />} />
                  <Route path="/creategenericnames" element={<CreateGenaricName />} />

                  <Route path="/vCreatePackages" element={<ViewPackage />} />
                  <Route path="/CreatePackages" element={<CreatePackages />} />

                  <Route path="/vCreateProducts" element={<ViewProduct />} />
                  <Route path="/CreateProducts" element={<CreateProducts />} />

                  <Route path="/vCreateBrands" element={<ViewBrands />} />
                  <Route path="/CreateBrands" element={<CreateBrands />} />

                  <Route path="/vCreateDocumentTypes" element={<ViewDocumentType />} />
                  <Route path="/CreateDocumentTypes" element={<CreateDocumentTypes />} />

                  <Route path="/Productdoc" element={<ProductDoc />} />

                  <Route path="/CreatreReason" element={<CreateReason />} />
                  <Route path="/vreasontypes" element={<ViewReason />} />

                  {/* Items Routes */}
                  <Route path="/items" element={<ViewCreateItem />} />
                  <Route path="/vCreateItem" element={<CreateItem />} />
                  <Route path="/viewitems" element={<ViewItemData />} />
                 
                  <Route path="/vAddVariant" element={<AddVariant />} />
                  <Route path="/vCreateVariant" element={<CreateVariant />} />

                  <Route path="/item-images" element={<ItemListView />} />
                  <Route path="/data-migration" element={<DataMigration />} />
                  <Route path="/unverified-items" element={<UnverifiedItems />} />
                </Routes>
              </Box>
            </Box>
          )}
        </>
      </PageDataProvider>
    </ThemeProvider>
  );
};

export default PageRouting;